import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Calendar, Clock, Tag, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const response = await axios.get(`${API}/blogs/${slug}`);
        setBlog(response.data);
      } catch (error) {
        console.error('Error fetching blog:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchBlog();
  }, [slug]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleBackToBlog = (e) => {
    e.preventDefault();
    navigate('/blog');
  };

  if (loading) {
    // Set document title directly during loading
    document.title = 'Loading Article | Manikandan Sivasankar';
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading article...</p>
        </div>
      </div>
    );
  }

  // Safety check - blog must have a title
  if (!blog || !blog.title) {
    document.title = 'Article Not Found | Manikandan Sivasankar';
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Article Not Found</h1>
          <button onClick={handleBackToBlog} className="text-blue-600 hover:underline">← Back to Blog</button>
        </div>
      </div>
    );
  }

  // Prepare SEO values
  const pageTitle = String(blog.title) + ' | Manikandan Sivasankar Blog';
  const pageDescription = blog.excerpt || (blog.title + ' - Insights from Manikandan Sivasankar');
  const pageKeywords = blog.tags ? (blog.tags.join(', ') + ', Manikandan Sivasankar') : 'Project Management, Agile';
  const pageUrl = 'https://manikandansivasankar.com/blog/' + slug;

  // Set document title directly to avoid Helmet issues
  document.title = pageTitle;

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <meta name="description" content={pageDescription} />
        <meta name="author" content={blog.author || 'Manikandan Sivasankar'} />
        <meta name="keywords" content={pageKeywords} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="article" />
      </Helmet>
      <Navigation />
      
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12">
        {/* Back Button */}
        <button 
          onClick={handleBackToBlog}
          className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-8 font-medium cursor-pointer"
          data-testid="back-to-blog-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Blog
        </button>

        {/* Category Badge */}
        <span className="inline-block bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium mb-4">
          {blog.category}
        </span>

        {/* Title */}
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight" data-testid="blog-title">
          {blog.title}
        </h1>

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-6 text-gray-500 mb-8 pb-8 border-b border-gray-200">
          <span className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            {formatDate(blog.published_date)}
          </span>
          <span className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            {blog.read_time || 5} min read
          </span>
          <span className="text-gray-600 font-medium">
            By {blog.author || 'Manikandan Sivasankar'}
          </span>
        </div>

        {/* Content */}
        <div 
          className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-600 prose-a:text-blue-600 prose-strong:text-gray-900 prose-ul:text-gray-600 prose-li:text-gray-600"
          dangerouslySetInnerHTML={{ __html: blog.content }}
        />

        {/* Tags */}
        {blog.tags && blog.tags.length > 0 && (
          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="flex items-center gap-2 mb-4">
              <Tag className="w-5 h-5 text-gray-400" />
              <span className="text-gray-600 font-medium">Tags</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {blog.tags.map((tag, index) => (
                <span 
                  key={index}
                  className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Author Card */}
        <div className="mt-12 bg-gray-50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">About the Author</h3>
          <p className="text-gray-600">
            {blog.author || 'Manikandan Sivasankar'} is a Strategic Project Manager with 16+ years of experience 
            in Agile transformation, digital strategy, and enterprise program management across Banking, 
            Insurance, and Healthcare industries.
          </p>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default BlogDetailPage;
